import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Form, Select, Input, DatePicker, Button, Space, Table, InputNumber, message, Checkbox, Popconfirm, Alert } from 'antd';
import { PlusOutlined, DeleteOutlined, UploadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Customer, OrderStatus, CustomerOrder, Product } from '../types.ts';
import { productAPI, shippingDestinationAPI } from '../services/api.ts';
import { formatCurrency, inputNumberFormatter, inputNumberParser } from '../utils/numberFormatting.ts';
import apiClient from '../utils/axiosConfig.ts';
import SilverseaExcelImportModal from './SilverseaExcelImportModal.tsx';
import ProductInventoryInfoModal from './ProductInventoryInfoModal.tsx';
import dayjs from 'dayjs';

const SILVERSEA_CUSTOMER_ID = 9;

const { Option } = Select;
const { TextArea } = Input;

interface CustomerOrderFormProps {
  initialValues?: CustomerOrder | null;
  customers: Customer[];
  orderStatuses: OrderStatus[];
  onSubmit: (values: any) => void;
  onCancel: () => void;
  /** Ordine evaso/in spedizione: righe in sola lettura, si salvano solo testata e dati di fatturazione */
  headerOnly?: boolean;
}

const CustomerOrderForm: React.FC<CustomerOrderFormProps> = ({
  initialValues,
  customers,
  orderStatuses,
  onSubmit,
  onCancel,
  headerOnly = false,
}) => {
  const [form] = Form.useForm();
  const [products, setProducts] = useState<Product[]>([]);
  const [shippingDestinations, setShippingDestinations] = useState<any[]>([]);
  const [orderDetails, setOrderDetails] = useState<any[]>([]);
  const [filterByCustomer, setFilterByCustomer] = useState(true);
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);
  const [isImportModalVisible, setIsImportModalVisible] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [infoProductId, setInfoProductId] = useState<number | null>(null);
  const [infoProductName, setInfoProductName] = useState<string>('');

  const loadProducts = useCallback(async (customerId?: number) => {
    try {
      const data = await productAPI.getProducts(undefined, customerId);
      setProducts(data);
    } catch (error) {
      message.error('Errore nel caricamento dei prodotti');
    }
  }, []);


  useEffect(() => {
    if (initialValues) {
      setSelectedCustomerId(initialValues.customer_id);
      loadShippingDestinations(initialValues.customer_id);
      setOrderDetails(initialValues.order_details || []);
      form.setFieldsValue({
        ...initialValues,
        order_date: initialValues.order_date ? dayjs(initialValues.order_date) : null,
        requested_delivery_date: initialValues.requested_delivery_date ? dayjs(initialValues.requested_delivery_date) : null,
        invoice_date: initialValues.invoice_date ? dayjs(initialValues.invoice_date) : null,
        t1_date: initialValues.t1_date ? dayjs(initialValues.t1_date) : null,
      });
    }
  }, [initialValues, form]);

  // Imposta lo stato "Nuovo" di default quando si crea un nuovo ordine
  useEffect(() => {
    if (!initialValues && orderStatuses.length > 0) {
      const newStatus = orderStatuses.find(s => s.status_name === 'Nuovo');
      if (newStatus) {
        form.setFieldsValue({ order_status_id: newStatus.id });
      }
    }
  }, [initialValues, orderStatuses, form]);

  // Carica prodotti quando cambia il filtro o il cliente selezionato
  useEffect(() => {
    const customerIdToUse = filterByCustomer ? selectedCustomerId : undefined;
    loadProducts(customerIdToUse);
  }, [filterByCustomer, selectedCustomerId, loadProducts]);


  const loadShippingDestinations = async (customerId: number) => {
    try {
      const data = await shippingDestinationAPI.getShippingDestinations('', customerId);
      setShippingDestinations(data);
    } catch (error) {
      console.error('Error loading shipping destinations:', error);
    }
  };

  const handleCustomerChange = (customerId: number) => {
    setSelectedCustomerId(customerId);
    loadShippingDestinations(customerId);
    form.setFieldValue('shipping_destination_id', undefined);
  };

  const addOrderDetail = () => {
    const newDetail = {
      id: Date.now(),
      product_id: undefined,
      quantity: 1,
      unit_price: 0,
      total_price: 0,
      notes: '',
    };
    setOrderDetails([...orderDetails, newDetail]);
  };

  const removeOrderDetail = (index: number) => {
    const newDetails = orderDetails.filter((_, i) => i !== index);
    setOrderDetails(newDetails);
  };

  const updateOrderDetail = (index: number, field: string, value: any) => {
    const newDetails = [...orderDetails];
    newDetails[index] = { ...newDetails[index], [field]: value };
    
    if (field === 'quantity' || field === 'unit_price') {
      newDetails[index].total_price = newDetails[index].quantity * newDetails[index].unit_price;
    }
    
    // Auto-fill price from product data
    if (field === 'product_id') {
      const product = products.find(p => p.id === value);
      if (product && product.price) {
        newDetails[index].unit_price = product.price;
        newDetails[index].total_price = newDetails[index].quantity * product.price;
      }
    }
    
    setOrderDetails(newDetails);
  };





  const handleExcelImport = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset input per permettere ri-selezione stesso file
    event.target.value = '';

    const allowedExtensions = ['.xls', '.xlsx'];
    const extension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!allowedExtensions.includes(extension)) {
      message.error('Formato file non supportato. Formati consentiti: .xls, .xlsx');
      return;
    }

    setImportLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post(
        '/customer-order-import/parse-silversea-excel',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      const result = response.data;

      if (result.matched_products.length === 0 && result.needs_resolution.length === 0) {
        message.warning('Nessun prodotto trovato nel file Excel');
        return;
      }

      if (result.needs_resolution.length > 0 || result.not_found.length > 0 || result.total_mismatch_warnings.length > 0) {
        // Mostra modale di risoluzione/riepilogo
        setImportResult(result);
        setIsImportModalVisible(true);
      } else {
        // Tutti i prodotti matchati senza problemi: popola direttamente
        populateOrderDetails(result.matched_products, {});
        message.success(`Importati ${result.matched_products.length} prodotti dal file Excel`);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Errore durante il parsing del file';
      message.error(errorMessage);
    } finally {
      setImportLoading(false);
    }
  };

  const populateOrderDetails = (matchedProducts: any[], resolvedProducts: Record<number, number>) => {
    const newDetails: any[] = [];

    // Aggiungi prodotti matchati automaticamente
    for (const product of matchedProducts) {
      newDetails.push({
        id: Date.now() + Math.random(),
        product_id: product.product_id,
        quantity: product.quantity,
        unit_price: product.unit_price,
        total_price: product.total_price,
        notes: '',
      });
    }

    // Aggiungi prodotti risolti dall'utente
    if (importResult?.needs_resolution) {
      for (const item of importResult.needs_resolution) {
        const chosenProductId = resolvedProducts[item.row];
        if (chosenProductId) {
          newDetails.push({
            id: Date.now() + Math.random(),
            product_id: chosenProductId,
            quantity: item.quantity,
            unit_price: item.unit_price,
            total_price: item.total_price,
            notes: '',
          });
        }
      }
    }

    setOrderDetails(newDetails);

    // Se il filtro per cliente e' attivo, disattivalo per mostrare tutti i prodotti nel Select
    if (filterByCustomer) {
      setFilterByCustomer(false);
    }
  };

  const handleImportConfirm = (resolutions: Record<number, number>) => {
    if (importResult) {
      populateOrderDetails(importResult.matched_products, resolutions);
      const totalImported = importResult.matched_products.length + Object.keys(resolutions).length;
      message.success(`Importati ${totalImported} prodotti dal file Excel`);
    }
    setIsImportModalVisible(false);
    setImportResult(null);
  };

  const handleImportCancel = () => {
    setIsImportModalVisible(false);
    setImportResult(null);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const submitData: any = {
        ...values,
        order_date: values.order_date?.format('YYYY-MM-DD'),
        requested_delivery_date: values.requested_delivery_date?.format('YYYY-MM-DD'),
        // Date di fatturazione: null esplicito = svuotamento lato server
        invoice_date: values.invoice_date ? values.invoice_date.format('YYYY-MM-DD') : null,
        t1_date: values.t1_date ? values.t1_date.format('YYYY-MM-DD') : null,
      };
      // Ordine bloccato (evaso/in spedizione): le righe non si inviano, il backend le
      // rifiuterebbe (e un array vuoto cancellerebbe tutte le righe di un ordine sbloccato)
      if (!headerOnly) {
        if (orderDetails.some((d) => !d.product_id)) {
          message.error('Ogni riga deve indicare un prodotto');
          return;
        }
        const ids = orderDetails.map((d) => d.product_id);
        if (new Set(ids).size !== ids.length) {
          message.error('Lo stesso prodotto compare su più righe: ogni prodotto può comparire una sola volta');
          return;
        }
        submitData.order_details = orderDetails.map(detail => ({
          product_id: detail.product_id,
          quantity: detail.quantity,
          unit_price: detail.unit_price,
          total_price: detail.total_price,
          notes: detail.notes,
        }));
      }
      onSubmit(submitData);
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const formatProductDisplay = (product: Product): string => {
    let display = product.internal_product_code;
    
    if (product.size) {
      display += `-${product.size}`;
    } else {
      display += '-';
    }
    
    if (product.color) {
      display += `-${product.color}`;
    } else {
      display += '-';
    }
    
    display += ' - ';
    
    if (product.client_product_code) {
      display += product.client_product_code;
    }
    
    display += ` - ${product.product_description}`;
    
    return display;
  };

  const detailColumns = [
    {
      title: 'Prodotto',
      dataIndex: 'product_id',
      width: '45%',
      ellipsis: true,
      render: (_: any, record: any, index: number) => {
        const selectedProduct = products.find(p => p.id === record.product_id);
        return (
          <Select
            value={record.product_id}
            onChange={(value) => updateOrderDetail(index, 'product_id', value)}
            style={{ width: '100%' }}
            placeholder="Seleziona prodotto"
            showSearch
            optionFilterProp="children"
            disabled={headerOnly}
          >
            {products.map((product) => (
              <Option
                key={product.id}
                value={product.id}
                title={formatProductDisplay(product)}
                // un prodotto può comparire una sola volta nell'ordine (il server lo rifiuta)
                disabled={orderDetails.some((d, i) => i !== index && d.product_id === product.id)}
              >
                {formatProductDisplay(product)}
              </Option>
            ))}
          </Select>
        );
      },
    },
    {
      title: 'Qta',
      dataIndex: 'quantity',
      width: '8%',
      render: (_: any, record: any, index: number) => (
        <InputNumber
          value={record.quantity}
          onChange={(value) => updateOrderDetail(index, 'quantity', value || 0)}
          min={1}
          style={{ width: '100%' }}
          size="small"
          disabled={headerOnly}
        />
      ),
    },
    {
      title: 'Vendita Unit.',
      dataIndex: 'unit_price',
      width: '12%',
      render: (_: any, record: any, index: number) => (
        <InputNumber
          value={record.unit_price}
          onChange={(value) => updateOrderDetail(index, 'unit_price', value || 0)}
          disabled={headerOnly}
          min={0}
          precision={4}
          step={0.0001}
          style={{ width: '100%' }}
          formatter={inputNumberFormatter}
          parser={inputNumberParser}
          placeholder="0,0000"
          size="small"
        />
      ),
    },
    {
      title: 'Totale',
      dataIndex: 'total_price',
      width: '10%',
      render: (value: number) => `€ ${formatCurrency(value)}`,
    },
    {
      title: 'Azioni',
      width: '10%',
      render: (_: any, record: any, index: number) => (
        <Space size="small">
          {record.product_id && (
            <Button
              icon={<InfoCircleOutlined />}
              onClick={() => {
                const product = products.find(p => p.id === record.product_id);
                setInfoProductId(record.product_id);
                setInfoProductName(product ? formatProductDisplay(product) : '');
              }}
              size="small"
              type="text"
              title="Info Inventario"
            />
          )}
          {!headerOnly && (
            <Popconfirm
              title="Sei sicuro di voler rimuovere questa riga?"
              onConfirm={() => removeOrderDetail(index)}
              okText="Sì"
              cancelText="No"
            >
              <Button
                icon={<DeleteOutlined />}
                danger
                size="small"
                type="text"
              />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Form form={form} layout="vertical">
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {headerOnly && (
            <Alert
              type="info"
              showIcon
              message="Ordine evaso o in fase di spedizione"
              description="Si possono modificare solo la testata e i dati di fatturazione: le righe dei prodotti sono in sola lettura."
            />
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Form.Item
              label="Cliente"
              name="customer_id"
              rules={[{ required: true, message: 'Seleziona un cliente' }]}
            >
              <Select placeholder="Seleziona cliente" onChange={handleCustomerChange} disabled={headerOnly}>
                {customers.map((customer) => (
                  <Option key={customer.id} value={customer.id}>
                    {customer.company}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Numero Ordine"
              name="order_number"
              rules={[{ required: true, message: 'Inserisci numero ordine' }]}
            >
              <Input 
                placeholder="Numero ordine" 
                style={{ textTransform: 'uppercase' }}
                onChange={(e) => {
                  const upperValue = e.target.value.toUpperCase();
                  form.setFieldValue('order_number', upperValue);
                }}
              />
            </Form.Item>

            <Form.Item
              label="Numero Ordine Cliente"
              name="customer_order_number"
            >
              <Input 
                placeholder="Numero ordine cliente (opzionale)"
                style={{ textTransform: 'uppercase' }}
                onChange={(e) => {
                  const upperValue = e.target.value.toUpperCase();
                  form.setFieldValue('customer_order_number', upperValue);
                }}
              />
            </Form.Item>

            <Form.Item
              label="Data Ordine"
              name="order_date"
              rules={[{ required: true, message: 'Seleziona data ordine' }]}
            >
              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
            </Form.Item>

            <Form.Item
              label="Data Consegna Richiesta"
              name="requested_delivery_date"
            >
              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
            </Form.Item>

            <Form.Item
              label="Stato Ordine"
              name="order_status_id"
              rules={[{ required: true, message: 'Seleziona stato ordine' }]}
              tooltip="Lo stato non si modifica da qui: in creazione è 'Nuovo', in modifica usare l'azione 'Cambia Stato'."
            >
              <Select placeholder="Seleziona stato" disabled>
                {orderStatuses.map((status) => (
                  <Option key={status.id} value={status.id}>
                    {status.status_name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Destinazione Spedizione"
              name="shipping_destination_id"
            >
              <Select placeholder="Seleziona destinazione">
                {shippingDestinations.map((destination) => (
                  <Option key={destination.id} value={destination.id}>
                    {destination.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="reorder"
              valuePropName="checked"
              style={{ gridColumn: 'span 2' }}
            >
              <Checkbox disabled={headerOnly}>
                <strong>Ordine Stock</strong> - Forza creazione ordini fornitori anche se i prodotti sono disponibili in stock
              </Checkbox>
            </Form.Item>
          </div>

          {/* Sezione Dati Fatturazione */}
          <div style={{ marginTop: 24, marginBottom: 16 }}>
            <h4 style={{ margin: 0, marginBottom: 16, borderBottom: '1px solid #d9d9d9', paddingBottom: 8 }}>
              Dati di Fatturazione (Opzionali)
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
              <Form.Item
                label="Numero DDT"
                name="ddt_number"
                rules={[
                  {
                    max: 50,
                    message: 'Il numero DDT non può superare i 50 caratteri'
                  }
                ]}
              >
                <Input
                  placeholder="Inserisci numero DDT (opzionale)"
                  maxLength={50}
                />
              </Form.Item>

              <Form.Item
                label="Numero Fattura"
                name="invoice_number"
                rules={[
                  {
                    max: 50,
                    message: 'Il numero fattura non può superare i 50 caratteri'
                  }
                ]}
              >
                <Input
                  placeholder="Inserisci numero fattura (opzionale)"
                  maxLength={50}
                />
              </Form.Item>

              <Form.Item
                label="Data Fattura"
                name="invoice_date"
              >
                <DatePicker
                  placeholder="Seleziona data fattura (opzionale)"
                  format="DD/MM/YYYY"
                  style={{ width: '100%' }}
                />
              </Form.Item>

              <Form.Item
                label="Numero T1"
                name="t1_number"
                rules={[
                  {
                    max: 50,
                    message: 'Il numero T1 non può superare i 50 caratteri'
                  }
                ]}
              >
                <Input
                  placeholder="Inserisci numero T1 (opzionale)"
                  maxLength={50}
                />
              </Form.Item>

              <Form.Item
                label="Data T1"
                name="t1_date"
              >
                <DatePicker
                  placeholder="Seleziona data T1 (opzionale)"
                  format="DD/MM/YYYY"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <Checkbox
              checked={filterByCustomer}
              onChange={(e) => setFilterByCustomer(e.target.checked)}
              disabled={!selectedCustomerId}
            >
              <strong>Visualizza solo i prodotti associati al cliente</strong>
            </Checkbox>
          </div>

          <Form.Item label="Note" name="notes">
            <TextArea rows={3} placeholder="Note ordine..." />
          </Form.Item>

          <div>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3>Dettagli Ordine</h3>
              {!headerOnly && (
                <Space>
                  {selectedCustomerId === SILVERSEA_CUSTOMER_ID && (
                    <Button
                      icon={<UploadOutlined />}
                      onClick={handleExcelImport}
                      loading={importLoading}
                    >
                      Importa Prodotti da Excel
                    </Button>
                  )}
                  <Button icon={<PlusOutlined />} onClick={addOrderDetail}>
                    Aggiungi Prodotto
                  </Button>
                </Space>
              )}
            </div>
            
            <Table
              columns={detailColumns}
              dataSource={orderDetails}
              rowKey="id"
              pagination={false}
              size="small"
              scroll={{ x: 'max-content' }}
              bordered
              tableLayout="fixed"
            />
          </div>

          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={onCancel}>
                Annulla
              </Button>
              <Button type="primary" onClick={handleSubmit}>
                {initialValues ? 'Aggiorna' : 'Crea'} Ordine
              </Button>
            </Space>
          </div>
        </Space>
      </Form>

      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept=".xls,.xlsx"
        onChange={handleFileSelected}
      />

      {importResult && (
        <SilverseaExcelImportModal
          visible={isImportModalVisible}
          importResult={importResult}
          onConfirm={handleImportConfirm}
          onCancel={handleImportCancel}
        />
      )}

      <ProductInventoryInfoModal
        visible={infoProductId !== null}
        productId={infoProductId}
        productName={infoProductName}
        onClose={() => setInfoProductId(null)}
      />
    </div>
  );
};

export default CustomerOrderForm;